import { alpha, lighten } from '@mui/material/styles';
import { MEDIA_FRAME_ASPECT_RATIOS } from '../../image-fit';
import type { HeroStyles, LandingTokens, Sx } from '../types';
import { BREAKPOINTS, down } from './breakpoints';

const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140' viewBox='0 0 140 140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.035 0'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)'/%3E%3C/svg%3E\")";

const grid: Sx = {
  position: 'relative',
  zIndex: 1,
  display: 'grid',
  gap: '64px',
  alignItems: 'center',
  padding: '96px 0 110px',
  [down(BREAKPOINTS.hero)]: {
    gridTemplateColumns: '1fr',
    gap: '44px',
    padding: '72px 0 84px',
  },
};

export const heroStyles = (t: LandingTokens): HeroStyles => ({
  section: {
    position: 'relative',
    background: [
      `radial-gradient(1100px 520px at 82% -10%, ${alpha(t.brand, 0.26)}, transparent 60%)`,
      `radial-gradient(900px 600px at -10% 110%, ${alpha(t.brandInk, 0.45)}, transparent 55%)`,
      'linear-gradient(180deg, #0d141a, #101820 55%, #121c24)',
    ].join(', '),
    color: t.onDark,
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      opacity: 0.5,
      backgroundImage: GRAIN,
    },
  },
  grid: {
    split: { ...grid, gridTemplateColumns: '1.05fr .95fr' },
    single: { ...grid, gridTemplateColumns: '1fr' },
  },
  eyebrow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '.22em',
    textTransform: 'uppercase',
    color: lighten(t.brand, 0.25),
    marginBottom: '26px',
    '&::before': {
      content: '""',
      width: 26,
      height: 1,
      backgroundColor: 'currentColor',
      opacity: 0.7,
    },
  },
  headline: {
    fontFamily: t.serif,
    fontWeight: 380,
    fontSize: 'clamp(42px, 5.4cqi, 68px)',
    lineHeight: 1.06,
    letterSpacing: '-.01em',
    margin: '0 0 24px',
    [down(BREAKPOINTS.mobile)]: { fontSize: 36 },
  },
  lede: {
    fontSize: 18,
    maxWidth: '46ch',
    color: alpha(t.onDark, 0.72),
    margin: '0 0 36px',
    [down(BREAKPOINTS.mobile)]: { fontSize: 16 },
  },
  ctaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    flexWrap: 'wrap',
    [down(BREAKPOINTS.mobile)]: { '& > *': { width: '100%' } },
  },
  note: {
    marginTop: '26px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '9px',
    fontSize: '13.5px',
    color: alpha(t.onDark, 0.6),
  },
  noteIcon: { width: 15, height: 15, flex: 'none', color: lighten(t.brand, 0.2) },
  media: {
    position: 'relative',
    borderRadius: '22px',
    overflow: 'hidden',
    aspectRatio: MEDIA_FRAME_ASPECT_RATIOS.hero,
  },
});
